"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import EmptyNomboxLogo from "@/components/shared/EmptyNomboxLogo";
import { scaleVariants } from "@/lib/animations";
import { FOOD_ICONS, type FoodIcon } from "@/lib/categorization/icons";
import { cn } from "@/lib/utils";

import styles from "./LoadingState.module.css";

// Use the centralized food icon library
const FOOD_ITEMS = FOOD_ICONS;

/**
 * Generate random trajectory values for projectile motion
 * All food starts from above with wide spread and arcs into the box center (50%)
 * Uses CSS custom properties for responsive sizing
 */
function generateTrajectory(previousPositions: number[] = []) {
  // Generate a starting position that's sufficiently spread from recent positions
  let startLeft: number;
  let attempts = 0;
  const minSpacing = 20; // Minimum 15% spacing between items

  do {
    startLeft = 10 + Math.random() * 80; // 10% to 90%
    attempts++;

    // Check if this position is sufficiently far from recent positions
    const isSufficientlySpread = previousPositions.every(
      (prevPos) => Math.abs(startLeft - prevPos) >= minSpacing
    );

    if (isSufficientlySpread || attempts > 10) {
      // Either found a good position or tried enough times
      break;
    }
  } while (attempts <= 10);

  const startTop = -5 - Math.random() * 30; // -5% to -35%

  // Add small random spread around center point (±5% variation)
  const spreadX = (Math.random() - 0.5) * 10; // -5% to +5%
  const spreadY = (Math.random() - 1) * 10; // -10% to 0%

  // Rotation (spinning as it falls)
  const rotationDirection = Math.random() > 0.5 ? 1 : -1;
  const endRotation = rotationDirection * (270 + Math.random() * 180); // 270-450 degrees

  return {
    startLeft: `${startLeft}%`,
    startLeftValue: startLeft, // Store numeric value for tracking
    startTop: `${startTop}%`,
    spreadX,
    spreadY,
    endRotation: `${endRotation}deg`,
  };
}

interface Trajectory {
  startLeft: string;
  startLeftValue: number;
  startTop: string;
  spreadX: number;
  spreadY: number;
  endRotation: string;
}

/**
 * Single animated food item that regenerates its trajectory after each animation completes
 * Waits for coordinator signal before starting next animation
 */
function AnimatedFoodItem({
  foodIcon,
  shouldStart,
  onAnimationComplete,
  containerSize,
  recentPositions,
  onPositionGenerated,
}: {
  foodIcon: FoodIcon;
  shouldStart: boolean;
  onAnimationComplete: () => void;
  containerSize: number;
  recentPositions: number[];
  onPositionGenerated: (position: number) => void;
}) {
  const [trajectory, setTrajectory] = useState<Trajectory | null>(null);
  const [key, setKey] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleAnimationEnd = () => {
    setIsAnimating(false);
    onAnimationComplete();
  };

  // Start animation when coordinator signals
  useEffect(() => {
    if (shouldStart && !isAnimating) {
      const newTrajectory = generateTrajectory(recentPositions);
      setTrajectory(newTrajectory);
      setKey((prev) => prev + 1); // Force re-render to restart animation
      setIsAnimating(true);
      onPositionGenerated(newTrajectory.startLeftValue);
    }
    // Only trigger when shouldStart changes, not recentPositions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStart]);

  // Don't render if we've never had a trajectory
  if (!trajectory) {
    return null;
  }

  // Keep rendering during animation even if shouldStart becomes false
  if (!isAnimating && !shouldStart) {
    return null;
  }

  const iconSize = containerSize * 0.219;

  // Calculate the center point of the container (50%, 50%)
  const centerX = containerSize * 0.5;
  const centerY = containerSize * 0.5;

  // Parse the starting left position percentage
  const startLeftPercent = parseFloat(trajectory.startLeft);
  const startLeftPx = (startLeftPercent / 100) * containerSize;

  // Parse the starting top position percentage
  const startTopPercent = parseFloat(trajectory.startTop);
  const startTopPx = (startTopPercent / 100) * containerSize;

  // Apply random spread around center (convert percentage to pixels)
  const spreadXPx = (trajectory.spreadX / 100) * containerSize;
  const spreadYPx = (trajectory.spreadY / 100) * containerSize;

  // Calculate translation needed to move icon center to container center
  // Add spread variation to create natural clustering around center
  const endX = centerX - startLeftPx - iconSize / 2 + spreadXPx;
  const endY = centerY - startTopPx - iconSize / 2 + spreadYPx;

  return (
    <div
      key={key}
      className={styles.foodItem}
      style={
        {
          top: trajectory.startTop,
          left: trajectory.startLeft,
          "--end-x": `${endX}px`,
          "--end-y": `${endY}px`,
          "--end-rotation": trajectory.endRotation,
        } as React.CSSProperties
      }
      onAnimationEnd={handleAnimationEnd}
    >
      <foodIcon.icon
        className="text-text-secondary"
        style={{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          // color: `var(--${foodIcon.colorClass})`,
        }}
      />
    </div>
  );
}

/**
 * Timing coordinator that schedules food items with controlled stagger
 * Ensures continuous flow without overwhelming the animation
 * Keeps running infinitely by continuously checking the queue
 */
function useStaggeredTiming(itemCount: number) {
  const [activeItems, setActiveItems] = useState<Set<number>>(new Set());
  const queueRef = useRef<number[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef(true);

  // Schedule next item from queue - runs continuously
  const scheduleNext = useRef(() => {
    if (!isRunningRef.current) return;

    if (queueRef.current.length > 0) {
      const nextItem = queueRef.current.shift()!;
      setActiveItems((prev) => new Set(prev).add(nextItem));
    }

    // Keep scheduling regardless of queue state (infinite loop)
    // Random stagger (350-600ms for smooth continuous flow)
    const delay = 350 + Math.random() * 250;
    timeoutRef.current = setTimeout(scheduleNext.current, delay);
  });

  // When an item completes, add it back to queue
  const handleItemComplete = (itemIndex: number) => {
    setActiveItems((prev) => {
      const next = new Set(prev);
      next.delete(itemIndex);
      return next;
    });

    // Add back to queue with random delay (500-1000ms before it can start again)
    const reQueueDelay = 500 + Math.random() * 500;
    setTimeout(() => {
      if (isRunningRef.current) {
        queueRef.current.push(itemIndex);
      }
    }, reQueueDelay);
  };

  // Initialize queue and start scheduling
  useEffect(() => {
    // Initialize queue with all items
    queueRef.current = Array.from({ length: itemCount }, (_, i) => i);
    isRunningRef.current = true;

    // Start infinite scheduling loop
    scheduleNext.current();

    return () => {
      isRunningRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [itemCount]);

  return { activeItems, handleItemComplete };
}

/**
 * LoadingState - Animated loading with real Lucide food icons flying into empty Nombox
 * Features:
 * - Empty Nombox logo (just the box outline)
 * - 9 different food icons with continuously randomized positions
 * - Items arc down from above into the box center with natural parabolic motion
 * - Staggered timing ensures continuous flow without overwhelming
 * - Fully responsive via CSS custom properties
 *
 * Usage:
 * <LoadingState className="size-48" /> - default size
 * <LoadingState className="size-40 sm:size-48 md:size-56" /> - responsive sizing
 */
export function LoadingState({ className = "size-42 sm:size-48" }: { className?: string }) {
  const { activeItems, handleItemComplete } = useStaggeredTiming(FOOD_ITEMS.length);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState(192);

  // Track recent starting positions to ensure spread
  const recentPositionsRef = useRef<number[]>([]);
  const maxTrackedPositions = 4; // Track last 4 positions

  const handlePositionGenerated = (position: number) => {
    recentPositionsRef.current.push(position);
    // Keep only the most recent positions
    if (recentPositionsRef.current.length > maxTrackedPositions) {
      recentPositionsRef.current.shift();
    }
  };

  // Observe container size changes for responsive sizing
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const size = entry.contentRect.width;
        setContainerSize(size);
      }
    });

    observer.observe(containerRef.current);

    // Initial size
    const initialSize = containerRef.current.offsetWidth;
    if (initialSize > 0) {
      setContainerSize(initialSize);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="food-icon"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={scaleVariants}
        className="flex flex-col items-center justify-center h-full min-h-[300px]"
      >
        <div
          ref={containerRef}
          className={cn("relative", className)}
          style={
            {
              "--container-size": containerSize,
              "--logo-size": containerSize * 0.625, // 62.5% of container (120/192)
              "--icon-size": containerSize * 0.219, // 21.9% of container (42/192)
            } as React.CSSProperties
          }
        >
          {/* Central Empty Nombox */}
          <div className="absolute inset-0 flex items-center justify-center">
            <EmptyNomboxLogo className="text-text-secondary" />
          </div>

          {/* Food items with staggered timing */}
          {FOOD_ITEMS.map((foodIcon, index) => (
            <AnimatedFoodItem
              key={index}
              foodIcon={foodIcon}
              shouldStart={activeItems.has(index)}
              onAnimationComplete={() => handleItemComplete(index)}
              containerSize={containerSize}
              recentPositions={recentPositionsRef.current}
              onPositionGenerated={handlePositionGenerated}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
